/**
 * =============================================================================
 * Hook: useAssertNotSharedRoute
 * Versão: 1.0.0 (2026-06-02)
 *
 * Descrição:
 *   Guarda em tempo de DESENVOLVIMENTO que dispara erro no console quando a
 *   rota atual está em SHARED_ROUTES e está sendo renderizada dentro de um
 *   layout específico de módulo (CrmLayout, FinancialLayout, TasksLayout,
 *   ReferralLayout, ActivitiesLayout) em vez de ModularLayout.
 *
 *   Motivação:
 *   Rotas compartilhadas precisam de ModularLayout para que a sidebar siga o
 *   `activeModule` (ex.: ao abrir Workspaces a partir de Atividades, a sidebar
 *   deve permanecer a de Atividades). Layouts dedicados travam a sidebar no
 *   módulo errado e causam a bagunça visual descrita pelo usuário.
 *
 *   Em produção é no-op (zero custo).
 * =============================================================================
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SHARED_ROUTES } from '@/hooks/useActiveModule';
import { createLogger } from '@/lib/logger';

const logger = createLogger('SharedRouteGuard');

/**
 * Deve ser chamado dentro de cada layout de módulo dedicado.
 * @param layoutName - Nome do layout (apenas para mensagem de erro).
 */
export function useAssertNotSharedRoute(layoutName: string) {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const isShared = SHARED_ROUTES.some((route) => pathname.startsWith(route));
    if (isShared) {
      logger.error(
        `[SharedRouteMisuse] A rota "${pathname}" está em SHARED_ROUTES, ` +
          `mas está renderizada dentro de <${layoutName}>. ` +
          `Troque para <ModularLayout> em src/App.tsx para que a sidebar siga o módulo ativo. ` +
          `Ver SHARED_ROUTES em src/hooks/useActiveModule.ts.`,
      );
    }
  }, [pathname, layoutName]);
}
